export type SubscriptionDeepLinkAction =
  | "skip-next-order"
  | "pause-subscription";

export interface SubscriptionDeepLink {
  action: SubscriptionDeepLinkAction;
  subscriptionId: number | null;
}

const ACTION_VALUES: readonly SubscriptionDeepLinkAction[] = [
  "skip-next-order",
  "pause-subscription",
];

function parseAction(raw: string | null): SubscriptionDeepLinkAction | null {
  if (!raw) return null;
  return (ACTION_VALUES as readonly string[]).includes(raw)
    ? (raw as SubscriptionDeepLinkAction)
    : null;
}

function parseSubscriptionId(raw: string | null): number | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

export function readSubscriptionDeepLink(): SubscriptionDeepLink | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const action = parseAction(params.get("so_action"));
  if (!action) return null;
  return {
    action,
    subscriptionId: parseSubscriptionId(params.get("so_subscription_id")),
  };
}

/**
 * True when the URL requests the account-wide "merge subscriptions" flow
 * (`?so_action=merge-subscriptions`). Merge isn't a per-subscription action, so
 * it's read separately from {@link readSubscriptionDeepLink} rather than folded
 * into `SubscriptionDeepLinkAction`. Cleared by
 * {@link clearSubscriptionDeepLinkFromUrl}, which already strips `so_action`.
 */
export function readMergeDeepLink(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("so_action") === "merge-subscriptions";
}

/**
 * Size-up deep link from the Klaviyo CTA
 * (`?so_action=size-up-bundle&so_subscription_id=…`). Returns the target
 * subscription id when present and valid; null when the action isn't size-up
 * or the id is missing/unusable.
 */
export function readSizeUpDeepLink(): { subscriptionId: number } | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("so_action") !== "size-up-bundle") return null;
  const subscriptionId = parseSubscriptionId(params.get("so_subscription_id"));
  if (subscriptionId == null) return null;
  return { subscriptionId };
}

/**
 * The combine-email notification id (`?so_combine_nid`), set by the Klaviyo CTA.
 * Present only when the customer arrived from the "eligible to combine" email —
 * it is what separates email-driven combines from organic ones in PostHog.
 */
export function getCombineNotificationId(): string | null {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get(
    "so_combine_nid",
  );
  return value?.trim() ? value.trim() : null;
}

/**
 * The size-up-email notification id (`?so_sizeup_nid`), set by the Klaviyo CTA.
 * Separates email-driven size-ups from organic ones in PostHog.
 */
export function getSizeUpNotificationId(): string | null {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get(
    "so_sizeup_nid",
  );
  return value?.trim() ? value.trim() : null;
}

export function clearSubscriptionDeepLinkFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  let changed = false;
  if (url.searchParams.has("so_action")) {
    url.searchParams.delete("so_action");
    changed = true;
  }
  if (url.searchParams.has("so_subscription_id")) {
    url.searchParams.delete("so_subscription_id");
    changed = true;
  }
  if (url.searchParams.has("so_combine_nid")) {
    url.searchParams.delete("so_combine_nid");
    changed = true;
  }
  if (url.searchParams.has("so_sizeup_nid")) {
    url.searchParams.delete("so_sizeup_nid");
    changed = true;
  }
  if (!changed) return;
  const clean = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", clean);
}
